import { action, computed, event, observable, persist, runInAction, store } from '~/common/mobx.decorator'
import { appStore, authStore, masterStore, notifyStore, routerStore } from '~/stores'
import {
	ChangePassSvg,
	ContactSvg,
	CredentialingSvg,
	FindSvg,
	HomeSvg,
	HomeSelectedSvg,
	MyJobsSvg,
	MyJobsSelectedSvg,
	CredentialingSelectedSvg,
	PrivacyPolicySvg,
	ProfileNavSvg,
	SettingsSvg,
	UserPlusSvg,
	IcFolderSvg,
} from '~/components/icons'
import { EVENTS, PATHS } from '~/common/constants'
import {
	apolloClient,
	GET_COMPANY_INFO_QUERY,
	GET_WORKER_AVATAR,
	GET_WORKER_BASIC_INFO_QUERY,
	GET_WORKER_BEST_MATCHES_JOB_PARAM_QUERY,
	GET_WORKER_DETAIL_QUERY,
} from '~/common/apollo'
import { eventBus } from 'mobx-event-bus2'
import { FOOTER_LINKS } from '~/common/constants'
import { EVENT_CLICK_ID } from '~/common/tracking/event-click.constant'
import { BannerMyJobs } from '~/assets'
import { contactStore } from '~/features/care-contact-us/care-contact-us.store'

export const PATH_NAME = window.location.path

const FEATURE_TYPE = {
	activated: 'activated',
	nonActivated: 'nonActivated',
	public: 'public',
	common: 'common',
	notCompleted: 'notCompleted',
}

@store()
class CareStore {
	@observable showProfileSidebar
	@observable showOnboardReminderCard = true
	@observable customHeader = {}
	@observable worker = {}
	@observable workerBasicInfo = {}
	@observable companyConfig = {}
	@observable workerConfigInfo = {}
	@persist @observable workerAvatar = null
	@persist @observable workerBestMatchesJobParam = null

	@computed
	get privacyPolicyUrl() {
		return appStore.config?.companyConfigs?.privacyPolicyUrl
	}

	@computed
	get termsOfUseUrl() {
		return appStore.config?.companyConfigs?.termUrl
	}

	@computed
	get facebookUrl() {
		return this.companyInfo.facebookUrl
	}

	@computed
	get instagramUrl() {
		return this.companyInfo.instagramUrl
	}

	@computed
	get twitterUrl() {
		return this.companyInfo.twitterUrl
	}

	@computed
	get linkedinUrl() {
		return this.companyInfo.linkedInUrl
	}

	@computed
	get noSms() {
		return this.worker.noSms
	}

	@computed
	get enableSmsSender() {
		return this.worker?.enableSmsSender
	}

	@computed
	get messagingServiceSender() {
		return this.companyConfig?.messagingServiceSender
	}

	@computed
	get privacyPolicyContent() {
		return this.companyConfig?.privacyPolicyContent
	}

	@computed
	get features() {
		const publicMenu = this.getFeature(FEATURE_TYPE.public)
		const activatedMenu = this.getFeature(FEATURE_TYPE.activated)
		const nonActivatedMenu = this.getFeature(FEATURE_TYPE.nonActivated)
		const notCompleted = this.getFeature(FEATURE_TYPE.notCompleted)

		if (authStore.requireCompleteProfile) {
			return [...notCompleted, ...publicMenu].filter((feature, index, self) => self.findIndex((t) => t.code === feature.code) === index)
		}
		const final = authStore.activated ? [...activatedMenu, ...publicMenu] : [...nonActivatedMenu, ...publicMenu]
		// Remove duplicated features
		return final.filter((feature, index, self) => self.findIndex((t) => t.code === feature.code) === index)
	}

	@computed
	get allFeatures() {
		const common = this.getFeature(FEATURE_TYPE.common)
		const allFeature = [...this.features, ...common]

		return allFeature?.find((feature) => feature.path === routerStore.basePath)
	}

	@computed
	get activeFeature() {
		return this.features?.find((feature) => feature.path === window.location.pathname)
	}

	@computed
	get code() {
		return this.getActiveFeature()?.code
	}

	@computed
	get name() {
		return this.getActiveFeature()?.name || ''
	}

	@computed
	get showOnboardReminder() {
		return this.getActiveFeature()?.showOnboardReminder && this.showOnboardReminderCard
	}

	@computed
	get getReferalRewardContent() {
		return this.companyConfig.referalRewardContent
	}

	@computed
	get sidebarMenu() {
		return this.features?.filter((feature) => feature.showSideBar)
	}

	@computed
	get dropDownMenu() {
		return this.features?.filter((feature) => feature.showDropdown)
	}

	@computed
	get dropDownMenuProfileNotComplete() {
		return this.features?.filter((feature) => feature.showDropdown)
	}

	@computed
	get appbarMenu() {
		return this.features?.filter((feature) => feature.showAppBar)
	}

	@computed
	get companyInfo() {
		return this.company
	}

	@computed
	get companyName() {
		return this.companyInfo?.name
	}

	@action
	getActiveFeature = () => {
		return (
			this.features?.find((feature) => {
				return window.location.pathname.includes(feature.path)
			}) || ''
		)
	}

	@action
	fetchWorkerDetail = async () => {
		const { data } = await apolloClient.query({
			query: GET_WORKER_DETAIL_QUERY,
			variables: { id: authStore.id },
		})
		this.worker = data.worker
	}

	@action
	fetchWorkerBasicInfo = async () => {
		try {
			const { data } = await apolloClient.query({
				query: GET_WORKER_BASIC_INFO_QUERY,
				variables: { id: authStore.id },
			})

			if (!data) throw new Error('No Data')
			const { worker } = data || {}
			this.workerBasicInfo = worker || {}

			authStore.setWorkAuthorized(worker.workAuthorizedAt)

			// Contact Us
			contactStore.contactAdvisor = worker?.employer
			contactStore.contactCaseManager = worker?.secondEmployer
		} catch (e) {
			console.log(e)
		}
	}

	@action
	fetchWorkerBestMatchesJobParam = async () => {
		const { data } = await apolloClient.query({
			query: GET_WORKER_BEST_MATCHES_JOB_PARAM_QUERY,
		})
		const worker = data?.worker
		let workerBestMatchesJobParam = {}

		if (worker.workerDisciplineSpecialties && worker.workerDisciplineSpecialties.length > 0) {
			const disciplineIds = worker.workerDisciplineSpecialties.map((item) => item.discipline.id)
			const specialtyIds = worker.workerDisciplineSpecialties.map((item) => item.specialty.id)
			if (disciplineIds.length > 0) workerBestMatchesJobParam.disciplineIds = disciplineIds
			if (specialtyIds.length > 0) workerBestMatchesJobParam.specialtyIds = specialtyIds
		}

		if (worker.educationLevel) {
			workerBestMatchesJobParam.educationLevel = worker.educationLevel
		}

		if (worker.yearsOfTotalExperience) {
			workerBestMatchesJobParam.minYearsOfExperience = {
				from: null,
				to: worker.yearsOfTotalExperience,
			}
		}

		runInAction(() => {
			this.workerBestMatchesJobParam = workerBestMatchesJobParam
		})
	}

	@action
	fetchWorkerAvatar = async () => {
		const { data } = await apolloClient.query({
			query: GET_WORKER_AVATAR,
		})
		this.workerAvatar = data?.worker?.avatar?.file?.thumbnails?.s_100x100
	}

	@action
	fetchCompanyInfo = async () => {
		const { data } = await apolloClient.query({ query: GET_COMPANY_INFO_QUERY })
		this.companyConfig = {
			linkedInUrl: data?.appInit?.companyConfig?.linkedInUrl,
			twitterUrl: data?.appInit?.companyConfig?.twitterUrl,
			instagramUrl: data?.appInit?.companyConfig?.instagramUrl,
			facebookUrl: data?.appInit?.companyConfig?.facebookUrl,
			defaultLocationImageWebUrl: data?.appInit?.companyConfig?.defaultLocationImageWebUrl,
			defaultLocationImageThumbnailUrl: data?.appInit?.companyConfig?.defaultLocationImageThumbnailUrl,
			privacyPolicyContent: data?.appInit?.companyConfig?.privacyPolicyContent,
			referalRewardContent: data?.appInit?.companyConfig?.referalRewardContent,
			messagingServiceSender: data?.appInit?.companyConfig?.messagingServiceSender,
		}
		this.company = {
			email: data?.appInit?.company?.mainContactEmail,
			phone: data?.appInit?.company?.phone,
			name: data?.appInit?.company?.name,
		}
		this.workerConfigInfo = {
			hasActiveAssignment: data?.appInit?.worker?.hasActiveAssignment,
			referralCode: data?.appInit?.worker?.referralCode,
		}
	}

	getFeature = (type) => {
		const activatedMenu = [
			{
				code: 'home',
				name: 'HOME',
				path: PATHS.care.home,
				icon: HomeSvg,
				selectedIcon: HomeSelectedSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useBanner: false,
				useLayout: true,
				eventId: EVENT_CLICK_ID.clickHomeNavBtn,
			},
			{
				code: 'resource-center',
				name: 'RESOURCES',
				path: PATHS.care.resource_center,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				isDisabled: false,
				useLayout: false,
				bannerUrl: undefined,
				icon: IcFolderSvg,
				useBanner: false,
			},
			{
				code: 'my-jobs',
				name: 'MY_JOBS',
				path: PATHS.care.my_jobs,
				icon: MyJobsSvg,
				selectedIcon: MyJobsSelectedSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				isDisabled: false,
				useBanner: true,
				eventId: EVENT_CLICK_ID.clickMyJobsNavBtn,
				bannerUrl: PATHS.banners.third,
			},
			{
				code: 'credential',
				name: 'CREDENTIALS',
				path: PATHS.care.credential,
				eventId: '',
				icon: CredentialingSvg,
				selectedIcon: CredentialingSelectedSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				showDropdown: false,
				isDisabled: false,
				useBanner: false,
				externalUrl: '#', // TODO: update code base to use externalUrl
			},
			{
				code: 'skill-checklists',
				name: 'SKILL_CHECKLISTS',
				path: PATHS.care.skill_checklist,
				eventId: '',
				icon: CredentialingSvg,
				selectedIcon: CredentialingSelectedSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				showDropdown: false,
				isDisabled: false,
				useBanner: false,
				externalUrl: '#', // TODO: update code base to use externalUrl
				parentCode: 'credential',
			},
			{
				code: 'profile',
				name: 'PROFILE',
				path: PATHS.care.profile,
				icon: ProfileNavSvg,
				eventId: EVENT_CLICK_ID.clickProfileNavBtn,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				showDropdown: true,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.third,
				useSecondLayout: true,
			},
			{
				code: 'change-password',
				name: 'CHANGE_PASSWORD',
				icon: ChangePassSvg,
				path: PATHS.common.change_password,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				showDropdown: true,
				bannerUrl: PATHS.banners.third,
			},
			{
				code: 'privacy-policy',
				name: 'PRIVACY_POLICY',
				icon: PrivacyPolicySvg,
				path: FOOTER_LINKS.PRIVACY_POLICY,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				showDropdown: true,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'contact-us',
				name: 'CONTACT_US',
				icon: ContactSvg,
				path: PATHS.care.contact_us,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				showDropdown: true,
				useBanner: false,
				bannerUrl: PATHS.banners.four,
				useSecondLayout: true,
			},
			{
				code: 'settings',
				name: 'SETTINGS',
				path: PATHS.care.settings,
				eventId: '',
				icon: SettingsSvg,
				auth: true,
				showDropdown: true,
				isDisabled: false,
				useBanner: false,
			},
			// Note: consider removing those features below
			{
				code: 'job-results',
				name: 'SEARCH_JOBS',
				path: PATHS.care.search,
				icon: FindSvg,
				auth: true,
				showAppBar: false,
				eventId: EVENT_CLICK_ID.clickSearchJobsNavBtn,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'filter-job',
				name: 'SEARCH_JOBS',
				path: PATHS.care.filter,
				icon: FindSvg,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'edit-filter-job',
				name: 'SEARCH_JOBS',
				path: PATHS.care.edit_filter,
				icon: FindSvg,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'job-results',
				name: 'SEARCH_RESULTS',
				path: PATHS.care.search,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'referrer',
				name: 'REFERRER',
				icon: UserPlusSvg,
				path: PATHS.care.referrer,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},

			{
				code: 'update-phone',
				name: 'UPDATE_PHONE_NUMBER',
				icon: ChangePassSvg,
				path: PATHS.common.update_phone,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},

			{
				code: 'popular-cities',
				name: 'POPULAR_CITIES',
				path: PATHS.care.popular_cities,
				icon: MyJobsSvg,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'explore-matches',
				name: 'EXPLORE_MATCHES',
				path: PATHS.care.suggested_jobs,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.second,
			},
			{
				code: 'urgent-needs',
				name: 'URGENT_NEEDS',
				path: PATHS.care.urgent_needs,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
			},
			{
				code: 'trending-jobs',
				name: 'TRENDING_JOBS',
				path: PATHS.care.trending_jobs,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.second,
			},
			{
				code: 'job-alerts',
				name: 'NOTIFICATIONS',
				path: PATHS.care.job_alerts,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.second,
			},

			{
				code: 'last-viewed',
				name: 'LAST_VIEWED',
				path: PATHS.care.last_viewed,
				icon: MyJobsSvg,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
			},
			{
				code: 'bookmark',
				name: 'BOOKMARKED_JOBS',
				path: PATHS.care.bookmark,
				icon: MyJobsSvg,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
			},
			{
				code: 'jobs',
				name: 'JOBS',
				path: PATHS.care.jobs,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
			},
			{
				code: 'discover',
				name: 'DISCOVER',
				path: PATHS.care.discover,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'signin',
				name: 'SIGNIN',
				path: PATHS.common.signin,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				useBanner: false,
			},
			{
				code: 'sign-out',
				name: 'SIGN_OUT',
				path: PATHS.care.sign_out,
				icon: 'user',
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: false,
			},
			{
				code: 'email-unsubscribe',
				name: 'EMAIL_UNSUBSCRIBE',
				path: PATHS.care.unsubscribe_emails,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
			},

			{
				code: 'verify-credential',
				name: 'VERIFY_CREDENTIAL',
				path: PATHS.common.verify_credential,
				icon: 'privacy',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				useLayout: false,
			},
		].filter(Boolean)

		const nonActivatedMenu = [
			{
				code: 'home',
				name: 'HOME',
				path: PATHS.care.home,
				icon: HomeSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useBanner: false,
				useLayout: true,
				eventId: EVENT_CLICK_ID.clickHomeNavBtn,
			},
			{
				code: 'jobs',
				name: 'JOBS',
				path: PATHS.care.jobs,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
			},
			{
				code: 'job-alerts',
				name: 'NOTIFICATIONS',
				path: PATHS.care.job_alerts,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.second,
			},

			{
				code: 'update-phone',
				name: 'UPDATE_PHONE_NUMBER',
				icon: ChangePassSvg,
				path: PATHS.common.update_phone,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},

			{
				code: 'job-results',
				name: 'SEARCH_JOBS',
				path: PATHS.care.search,
				icon: FindSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				eventId: EVENT_CLICK_ID.clickSearchJobsNavBtn,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.second,
			},
			{
				code: 'filter-job',
				name: 'SEARCH_JOBS',
				path: PATHS.care.filter,
				icon: FindSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'job-results',
				name: 'SEARCH_RESULTS',
				path: PATHS.care.search,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'my-jobs',
				name: 'MY_JOBS',
				path: PATHS.care.my_jobs,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				isDisabled: false,
				useBanner: true,
				showNameOnBanner: false,
				eventId: EVENT_CLICK_ID.clickMyJobsNavBtn,
				bannerUrl: BannerMyJobs,
			},
			{
				code: 'edit-filter-job',
				name: 'SEARCH_JOBS',
				path: PATHS.care.edit_filter,
				icon: FindSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'referrer',
				name: 'REFERRER',
				icon: UserPlusSvg,
				path: PATHS.care.referrer,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},

			{
				code: 'popular-cities',
				name: 'POPULAR_CITIES',
				path: PATHS.care.popular_cities,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
				showOnboardReminder: true,
			},
			{
				code: 'explore-matches',
				name: 'EXPLORE_MATCHES',
				path: PATHS.care.suggested_jobs,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.second,
				showOnboardReminder: true,
			},
			{
				code: 'urgent-needs',
				name: 'URGENT_NEEDS',
				path: PATHS.care.urgent_needs,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
				showOnboardReminder: true,
			},
			{
				code: 'trending-jobs',
				name: 'TRENDING_JOBS',
				path: PATHS.care.trending_jobs,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.second,
				showOnboardReminder: true,
			},
			{
				code: 'last-viewed',
				name: 'LAST_VIEWED',
				path: PATHS.care.last_viewed,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
			},
			{
				code: 'bookmark',
				name: 'BOOKMARKED_JOBS',
				path: PATHS.care.bookmark,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
			},

			{
				code: 'credential',
				name: 'CREDENTIALS',
				path: PATHS.care.credential,
				eventId: '',
				icon: CredentialingSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				showDropdown: false,
				isDisabled: false,
				useBanner: false,
			},

			{
				code: 'jobs',
				name: 'JOBS',
				path: PATHS.care.jobs,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.first,
			},

			{
				code: 'discover',
				name: 'DISCOVER',
				path: PATHS.care.discover,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				bannerUrl: PATHS.banners.second,
				showOnboardReminder: true,
			},
			{
				code: 'sign-out',
				name: 'SIGN_OUT',
				path: PATHS.care.sign_out,
				icon: 'user',
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: false,
			},
			{
				code: 'email-unsubscribe',
				name: 'EMAIL_UNSUBSCRIBE',
				path: PATHS.care.email_unsubscribe,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: false,
				bannerUrl: PATHS.banners.second,
			},

			{
				code: 'verify-credential',
				name: 'VERIFY_CREDENTIAL',
				path: PATHS.common.verify_credential,
				icon: 'privacy',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				useLayout: false,
			},
		]

		const publicMenu = [
			{
				code: 'email-unsubscribe',
				name: 'EMAIL_UNSUBSCRIBE',
				path: PATHS.care.unsubscribe_emails,
				icon: MyJobsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'verify-credential',
				name: 'VERIFY_CREDENTIAL',
				path: PATHS.common.verify_credential,
				icon: 'privacy',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				useLayout: false,
			},
		].filter(Boolean)

		const common = [
			{
				code: 'signin',
				name: 'SIGNIN',
				path: PATHS.common.signin,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				useBanner: false,
			},
			{
				code: 'reset',
				name: 'RESET',
				path: PATHS.common.reset,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'errors',
				name: 'ERRORS',
				path: PATHS.common.errors,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
			},
			{
				code: 'email-unsubscribe',
				name: 'EMAIL_UNSUBSCRIBE',
				path: PATHS.care.email_unsubscribe,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: false,
				bannerUrl: PATHS.banners.second,
			},
			{
				code: 'verify-credential',
				name: 'VERIFY_CREDENTIAL',
				path: PATHS.common.verify_credential,
				icon: 'privacy',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useBanner: false,
				useLayout: false,
			},
		].filter(Boolean)

		let result = null
		switch (type) {
			case FEATURE_TYPE.activated:
				result = activatedMenu
				break
			case FEATURE_TYPE.nonActivated:
				result = nonActivatedMenu
				break
			case FEATURE_TYPE.public:
				result = publicMenu
				break
			case FEATURE_TYPE.common:
				result = common
				break
			case FEATURE_TYPE.notCompleted:
				result = common
				break
			default:
				result = []
				break
		}
		return result
	}

	@action
	toogleProfileSidebar = async () => {
		this.showProfileSidebar = !this.showProfileSidebar
	}

	@action
	toggleOnboardReminder = async () => {
		this.showOnboardReminderCard = !this.showOnboardReminderCard
	}

	@action
	logout = async () => {
		this.showProfileSidebar = false
		notifyStore.unInitOneSignal()

		eventBus.post(EVENTS.authStore.logout)
		routerStore.goPage(PATHS.common.signin)
	}

	@action
	handleClickChangePassword = async () => {
		this.showProfileSidebar = false
		await routerStore.goPage(PATHS.common.change_password)
	}

	@event(EVENTS.careStore.updateHeader)
	async onUpdateHeader({ payload }) {
		this.customHeader = payload
	}
}

export const careStore = new CareStore()
